/**
 * Utility for selecting a consistent, neutral, natural-sounding voice
 * across devices (Windows, Android, iOS, macOS) and browsers (Chrome, Edge, Safari, Firefox).
 */

const EXCLUDED_VOICE_KEYWORDS = [
  'child',
  'boy',
  'girl',
  'baby',
  'kid',
  'cartoon',
  'whisper',
  'robot',
  'novelty',
  'junior',
  'young',
  'xiaoshuang',
  '晓双',
  'yunxia',
  '云夏',
  'zarvox',
  'trinoids',
  'agnes',
  'albert',
  'bad news',
  'bahh',
  'bells',
  'boing',
  'bubbles',
  'cellos',
  'deranged',
  'good news',
  'hysterical',
  'organ',
  'superstar',
  'wobble',
  'jester',
];

// Preferred voice name fragments in order of preference for a natural, neutral tone
const PREFERRED_VOICES = {
  'zh-CN': [
    'tingting',
    'ting-ting',
    '婷婷',
    'huihui',
    '慧慧',
    'yaoyao',
    '瑶瑶',
    'kangkang',
    '康康',
    'xiaoxiao',
    '晓晓',
    'yunxi',
    '云希',
    'xiaoyi',
    '晓伊',
    'yunjian',
    '云健',
    'yunyang',
    '云扬',
    'natural',
    'mandarin',
    'chinese',
    '普通话',
    'google 普通话',
    'google',
  ],
  'zh-HK': [
    'sinji',
    'sin-ji',
    '善怡',
    'tracy',
    'danny',
    'hiugaai',
    '晓佳',
    '曉佳',
    'hiumaan',
    '晓曼',
    '曉曼',
    'wanlung',
    '云龙',
    '雲龍',
    'meijia',
    'mei-jia',
    '美佳',
    'hsiaochen',
    '晓臻',
    '曉臻',
    'yating',
    '雅婷',
    'natural',
    'cantonese',
    'hong kong',
    '粤语',
    '粵語',
    'google 粤语',
    'google 粵語',
    'google',
  ],
  'en-US': [
    'samantha',
    'alex',
    'ava',
    'allison',
    'zira',
    'david',
    'mark',
    'jenny',
    'aria',
    'guy',
    'natural',
    'google us english',
    'google',
    'standard',
  ],
};

/**
 * Normalizes a voice or browser language tag (e.g. 'zh_CN', 'zh-Hans-CN') to lowercase hyphenated format.
 */
export function normalizeLangTag(lang = '') {
  return String(lang || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
}

/**
 * Normalizes an i18n language string to standard BCP-47 speech tags.
 */
export function getSpeechLangCode(lang = 'zh-CN') {
  const normalized = normalizeLangTag(lang);
  if (normalized.includes('hk') || normalized.includes('tw') || normalized.includes('hant') || normalized.includes('yue')) {
    return 'zh-HK';
  }
  if (normalized.startsWith('en')) {
    return 'en-US';
  }
  return 'zh-CN';
}

// Maximum characters per utterance. Many engines (Chrome network voices, Android system TTS,
// Windows SAPI) silently truncate or stall on long utterances, so keep each chunk short.
const MAX_CHUNK_CJK = 70;
const MAX_CHUNK_LATIN = 160;
const SPEAKABLE_CHAR = /[^\s。！？.!?,，、；：;:…"'“”‘’()（）[\]【】《》\-—]/;

/**
 * Hard-splits a long string without punctuation into pieces no longer than `max`,
 * preferring word boundaries (spaces) for Latin text and never splitting surrogate pairs.
 */
function hardSplit(text, max) {
  const out = [];
  let rest = text.trim();
  while (rest.length > max) {
    let cut = rest.lastIndexOf(' ', max);
    if (cut < max * 0.5) cut = max;
    const prev = rest.charCodeAt(cut - 1);
    if (prev >= 0xd800 && prev <= 0xdbff) cut -= 1;
    out.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) out.push(rest);
  return out;
}

/**
 * Splits article text into short, speakable chunks.
 * - Sentence boundaries: 。！？!? , a period followed by whitespace/end (so "5.5" / URLs are not split), newlines.
 * - Over-long sentences are split at secondary punctuation (，、；： , ; :) and finally hard-split.
 */
export function splitIntoSpeechChunks(fullText = '') {
  if (!fullText || typeof fullText !== 'string') return [];

  const result = [];
  const push = (chunk) => {
    const trimmed = chunk.trim();
    if (trimmed && SPEAKABLE_CHAR.test(trimmed)) result.push(trimmed);
  };

  // With one capture group, split() alternates [body, delimiter, body, delimiter, ...]
  const pieces = fullText.split(/([。！？!?]+|\.(?=\s|$)|\n+)/g);
  const sentences = [];
  for (let i = 0; i < pieces.length; i += 2) {
    const body = pieces[i] || '';
    const delimiter = (pieces[i + 1] || '').replace(/\n+/g, '');
    const sentence = `${body}${delimiter}`.trim();
    if (sentence) sentences.push(sentence);
  }

  sentences.forEach((sentence) => {
    const max = /[\u3400-\u9fff]/.test(sentence) ? MAX_CHUNK_CJK : MAX_CHUNK_LATIN;
    if (sentence.length <= max) {
      push(sentence);
      return;
    }

    const parts = sentence.split(/([，、；：]|[,;:](?=\s))/g);
    let buffer = '';
    const flush = () => {
      if (!buffer) return;
      if (buffer.length > max) {
        hardSplit(buffer, max).forEach(push);
      } else {
        push(buffer);
      }
      buffer = '';
    };

    for (let i = 0; i < parts.length; i += 2) {
      const piece = `${parts[i] || ''}${parts[i + 1] || ''}`;
      if (buffer && (buffer + piece).length > max) flush();
      buffer += piece;
    }
    flush();
  });

  return result;
}

/**
 * Resolves the optimal speech language code by analyzing the article text
 * against the selected UI language.
 *
 * In Web Speech API, English voices (en-US) CANNOT pronounce Chinese characters (Hanzi).
 * If an English voice is assigned to Chinese text, the browser synthesizer silently skips
 * all Chinese characters and only pronounces isolated English words and numbers.
 * Conversely, Chinese voices (zh-CN, zh-HK) are bilingual and fluently speak
 * both Chinese text AND embedded English words/numbers.
 *
 * Therefore:
 * - If the content contains Chinese characters, use a Chinese voice (zh-CN or zh-HK)
 *   even when the user interface is set to English, ensuring the entire article is read aloud.
 * - If the content has no Chinese characters and contains English letters, use en-US.
 */
export function resolveContentSpeechLang(text = '', siteLang = 'zh-CN') {
  if (!text || typeof text !== 'string') {
    return siteLang;
  }

  // Count Chinese (CJK) characters in the text
  const cjkMatches = text.match(/[\u4e00-\u9fa5\u3400-\u4dbf]/g);
  const cjkCount = cjkMatches ? cjkMatches.length : 0;

  // If there are Chinese characters, an English voice will drop all Chinese text
  // and only read out numbers/English words. We must use a Chinese-capable voice.
  if (cjkCount > 0) {
    // If the site is in Traditional Chinese / HK, use zh-HK; otherwise use zh-CN
    return siteLang === 'zh-HK' ? 'zh-HK' : 'zh-CN';
  }

  // If there are no Chinese characters and there are Latin characters, use en-US
  if (/[a-zA-Z]/.test(text)) {
    return 'en-US';
  }

  return siteLang;
}

/**
 * Checks whether a voice's language tag matches the requested target language.
 */
function isExactLangMatch(vLang, normalizedTarget) {
  if (normalizedTarget === 'zh-hk') {
    return (
      vLang.includes('zh-hk') ||
      vLang.includes('yue') ||
      vLang.includes('hant-hk') ||
      vLang.includes('zh-mo') ||
      vLang.includes('zh-tw') ||
      vLang.includes('hant')
    );
  }
  if (normalizedTarget === 'zh-cn') {
    return (
      vLang.includes('zh-cn') ||
      vLang.includes('cmn') ||
      vLang.includes('hans') ||
      (vLang.startsWith('zh') &&
        !vLang.includes('hk') &&
        !vLang.includes('tw') &&
        !vLang.includes('mo') &&
        !vLang.includes('hant'))
    );
  }
  if (normalizedTarget === 'en-us') {
    return vLang.startsWith('en');
  }
  return vLang.startsWith(normalizedTarget.slice(0, 2));
}

/**
 * Checks whether a voice is any Chinese-capable voice (for fallback when a device
 * only has zh-CN installed while browsing in zh-HK, or vice versa, or when a domestic
 * browser reports Chinese in voice.name instead of voice.lang).
 */
function isAnyChineseVoice(voice) {
  const vLang = normalizeLangTag(voice?.lang);
  const vName = String(voice?.name || '').toLowerCase();
  return (
    vLang.startsWith('zh') ||
    vLang.includes('cmn') ||
    vLang.includes('yue') ||
    vLang.includes('hans') ||
    vLang.includes('hant') ||
    /chinese|mandarin|cantonese|普通话|国语|國語|粤语|粵語|中文|汉语|漢語|huihui|慧慧|yaoyao|瑶瑶|kangkang|康康|tingting|婷婷|sinji|善怡|xiaoxiao|晓晓|yunxi|云希/.test(
      vName
    )
  );
}

/**
 * Selects the best, most reliable neutral SpeechSynthesisVoice object for the given language.
 * Prioritizes local/offline OS voices (`localService: true`) so playback never silently fails
 * when cloud TTS endpoints (such as Google TTS in Mainland China) are unreachable, and allows
 * excluding voices that previously errored at runtime.
 *
 * @param {SpeechSynthesisVoice[]} voices - List of voices from window.speechSynthesis.getVoices()
 * @param {string} langCode - 'zh-CN' | 'zh-HK' | 'en-US'
 * @param {Set<string>|string[]} excludedURIs - Optional set/array of voiceURIs that failed previously
 * @returns {SpeechSynthesisVoice|undefined} Best matching voice object, or undefined
 */
export function selectNeutralVoice(voices = [], langCode = 'zh-CN', excludedURIs = null) {
  if (!Array.isArray(voices) || voices.length === 0) {
    return undefined;
  }

  const excludedSet =
    excludedURIs instanceof Set
      ? excludedURIs
      : Array.isArray(excludedURIs)
        ? new Set(excludedURIs)
        : new Set();

  const normalizedTarget = normalizeLangTag(langCode);

  // Filter out previously failed voices
  const availableVoices = voices.filter(
    (v) => v && v.name && (!v.voiceURI || !excludedSet.has(v.voiceURI))
  );

  if (availableVoices.length === 0) {
    return undefined;
  }

  // 1. Filter out known child / exaggerated / novelty voice names
  const candidateVoices = availableVoices.filter((v) => {
    const nameLower = v.name.toLowerCase();
    return !EXCLUDED_VOICE_KEYWORDS.some((kw) => nameLower.includes(kw));
  });

  const pool = candidateVoices.length > 0 ? candidateVoices : availableVoices;

  // 2. Filter candidate voices matching the target language
  let langMatchingVoices = pool.filter((v) => {
    const vLang = normalizeLangTag(v.lang);
    return isExactLangMatch(vLang, normalizedTarget);
  });

  // 3. Crucial for computers in Mainland China:
  // If target is Chinese (e.g. zh-HK on a Mainland Windows PC that only has local zh-CN voices,
  // while Chrome lists remote "Google 粤語（香港）" with localService: false which is blocked in China),
  // ensure local Chinese voices from the pool are included whenever exact matches lack a local OS voice.
  const hasLocalExactMatch = langMatchingVoices.some(
    (v) => v.localService === true && !String(v.name || '').toLowerCase().includes('google')
  );
  if (normalizedTarget.startsWith('zh') && (!hasLocalExactMatch || langMatchingVoices.length === 0)) {
    const allChinese = pool.filter((v) => isAnyChineseVoice(v));
    const merged = new Set([...langMatchingVoices, ...allChinese]);
    langMatchingVoices = Array.from(merged);
  }

  // 4. If still no match in filtered pool, try raw available voices matching the 2-letter prefix
  if (langMatchingVoices.length === 0) {
    const prefix = normalizedTarget.slice(0, 2);
    langMatchingVoices = availableVoices.filter(
      (v) =>
        normalizeLangTag(v.lang).startsWith(prefix) ||
        (prefix === 'zh' && isAnyChineseVoice(v))
    );
  }

  if (langMatchingVoices.length === 0) {
    return undefined;
  }

  // 5. Score voices according to reliability (localService) and natural/neutral quality
  const preferences = PREFERRED_VOICES[langCode] || PREFERRED_VOICES['zh-CN'];

  let bestVoice = langMatchingVoices[0];
  let highestScore = -Infinity;

  for (const voice of langMatchingVoices) {
    const nameLower = voice.name.toLowerCase();
    const vLang = normalizeLangTag(voice.lang);
    const isGoogleRemote = nameLower.includes('google') && voice.localService !== true;
    let score = 0;

    // Strongly prefer local OS voices (localService === true) because remote voices
    // (especially Chrome's "Google 普通话 / Google 粤語") require Google servers that are
    // unreachable in Mainland China and cause speechSynthesis to hang silently.
    if (voice.localService === true && !nameLower.includes('google')) {
      score += 160;
    } else if (isGoogleRemote) {
      // Heavily penalize remote Google TTS voices so any local SAPI/macOS or Microsoft voice wins first
      score -= 200;
    }

    // Exact dialect match bonus (e.g., zh-hk/yue over zh-cn when zh-HK is requested)
    if (normalizedTarget === 'zh-hk' && isExactLangMatch(vLang, 'zh-hk')) {
      score += 50;
    } else if (normalizedTarget === 'zh-cn' && isExactLangMatch(vLang, 'zh-cn')) {
      score += 50;
    } else if (normalizedTarget === 'en-us' && (vLang.includes('en-us') || vLang.includes('en-gb'))) {
      score += 30;
    }

    // Higher score for preferred voice names
    for (let i = 0; i < preferences.length; i++) {
      if (nameLower.includes(preferences[i])) {
        score += (preferences.length - i) * 6;
        break;
      }
    }

    // Bonus for default system voice
    if (voice.default) {
      score += 15;
    }

    if (score > highestScore) {
      highestScore = score;
      bestVoice = voice;
    }
  }

  return bestVoice;
}

/**
 * Selects the best, most neutral voiceURI for the given language from available voices.
 *
 * @param {SpeechSynthesisVoice[]} voices - List of voices from window.speechSynthesis.getVoices()
 * @param {string} langCode - 'zh-CN' | 'zh-HK' | 'en-US'
 * @param {Set<string>|string[]} excludedURIs - Optional set/array of voiceURIs to exclude
 * @returns {string|undefined} voiceURI of the best matching voice, or undefined for browser default
 */
export function selectNeutralVoiceURI(voices = [], langCode = 'zh-CN', excludedURIs = null) {
  const bestVoice = selectNeutralVoice(voices, langCode, excludedURIs);
  return bestVoice?.voiceURI;
}
